/**
 * CMS-first resolution for the content the platform pages render, with the
 * static registry as the fallback — same contract as productSource.ts.
 *
 * Every loader here returns usable content or the bundled default. None of them
 * throws and none returns empty, so no caller needs a loading or error branch,
 * and an API outage degrades a page rather than deleting it.
 */

import { getSolutions, getOffices, getSeoMeta, getTrust, getCertifications } from "./api";
import {
    SOLUTIONS_BY_INDUSTRY,
    SOLUTIONS_BY_CAPABILITY,
    SolutionItem,
} from "../data/productsData";
import { CERTIFICATIONS, Certification } from "../data/certificationsData";

export interface OfficeLocation {
    name: string;
    flag: string;
    address: string;
    phone: string;
}

export interface SeoRecord {
    path: string;
    title: string;
    description: string;
    image?: string;
    noIndex?: boolean;
    answer?: string;
    faqs?: { question: string; answer: string }[];
}

export interface TrustSection {
    key: string;
    title: string;
    body: string;
    points: string[];
}

export interface TrustDocument {
    sections: TrustSection[];
    certifications: { name: string; detail: string }[];
    securityContact: string;
}

const OFFICE_FALLBACK: OfficeLocation[] = [
    { name: "India", flag: "/assets/imgs/flags/ind_img.svg", address: "A/1109, Money Plant High Street, Jagatpur Road, Near S.G. Highway, Ahmedabad - 382470", phone: "+91 9429167971" },
    { name: "USA", flag: "/assets/imgs/flags/usa_img.svg", address: "400 Capital Circle SE, Suite 18281, Tallahassee, FL 32301. USA", phone: "" },
    { name: "Australia", flag: "/assets/imgs/flags/aus_img.svg", address: "1 King Street, Sydney, NSW 2000, Australia", phone: "" },
    { name: "UAE", flag: "/assets/imgs/flags/uae_img.svg", address: "Silicon Oasis, Dubai, UAE", phone: "" },
    { name: "UK", flag: "/assets/imgs/flags/uk_img.svg", address: "221B Baker Street, London, UK", phone: "" },
];

let cachedSolutions: { industry: SolutionItem[]; capability: SolutionItem[] } | null = null;
let cachedOffices: OfficeLocation[] | null = null;
let cachedSeo: Record<string, SeoRecord> | null = null;
let cachedTrust: TrustDocument | null = null;
let cachedCertifications: Certification[] | null = null;

export const getCachedSolutions = () => cachedSolutions;
export const getCachedOffices = () => cachedOffices;
export const getCachedSeoMap = () => cachedSeo;
export const getCachedTrust = () => cachedTrust;
export const getCachedCertifications = () => cachedCertifications;

const usableSolution = (r: any): boolean =>
    Boolean(r?.slug && r?.title && r?.description && (r.cut === "industry" || r.cut === "capability"));

/**
 * Solutions split by cut. FIRST priority from API.
 * Falls back to local static data only if the API is unreachable.
 */
export async function loadSolutions(): Promise<{
    industry: SolutionItem[];
    capability: SolutionItem[];
}> {
    if (cachedSolutions) return cachedSolutions;

    try {
        const rows = await getSolutions();
        if (Array.isArray(rows)) {
            const valid = rows.filter(usableSolution);
            const map = (cut: string): SolutionItem[] =>
                valid
                    .filter((r) => r.cut === cut)
                    .map((r) => ({
                        slug: r.slug,
                        title: r.title,
                        description: r.description,
                        productSlugs: Array.isArray(r.productSlugs) ? r.productSlugs : [],
                    }));

            const result = {
                industry: map("industry"),
                capability: map("capability"),
            };

            // If API answered and returned solutions, API is authoritative
            if (result.industry.length > 0 || result.capability.length > 0) {
                cachedSolutions = result;
                return result;
            }
        }
    } catch (err) {
        console.warn("Solutions API unreachable, falling back to local static data:", err);
    }

    return { industry: SOLUTIONS_BY_INDUSTRY, capability: SOLUTIONS_BY_CAPABILITY };
}

/**
 * Office locations. FIRST priority from API.
 * Falls back to local static data only if the API is unreachable.
 */
export async function loadOffices(): Promise<OfficeLocation[]> {
    if (cachedOffices) return cachedOffices;

    try {
        const rows = await getOffices();
        if (Array.isArray(rows) && rows.length > 0) {
            const valid = rows.filter((r) => r?.name && r?.address);
            if (valid.length > 0) {
                const mapped = valid.map((r) => ({
                    name: r.name,
                    flag: r.flag ?? "",
                    address: r.address,
                    phone: r.phone ?? "",
                }));
                cachedOffices = mapped;
                return mapped;
            }
        }
    } catch (err) {
        console.warn("Offices API unreachable, falling back to local static data:", err);
    }

    return OFFICE_FALLBACK;
}

/**
 * All SEO records, keyed by path. FIRST priority from API.
 * Falls back to empty map only if unreachable.
 */
export async function loadSeoMap(): Promise<Record<string, SeoRecord>> {
    if (cachedSeo) return cachedSeo;

    try {
        const rows = await getSeoMeta();
        if (Array.isArray(rows)) {
            const map: Record<string, SeoRecord> = {};
            for (const r of rows) {
                if (r?.path && r?.title && r?.description) {
                    map[r.path] = r;
                }
            }
            cachedSeo = map;
            return map;
        }
    } catch (err) {
        console.warn("SEO API unreachable, using local page metadata:", err);
    }

    return {};
}

/**
 * Security posture / trust document. FIRST priority from API.
 */
export async function loadTrust(): Promise<TrustDocument | null> {
    if (cachedTrust) return cachedTrust;

    try {
        const doc = await getTrust();
        if (doc && Array.isArray(doc.sections) && doc.sections.length > 0) {
            const mapped = {
                sections: doc.sections,
                certifications: Array.isArray(doc.certifications) ? doc.certifications : [],
                securityContact: doc.securityContact ?? "",
            };
            cachedTrust = mapped;
            return mapped;
        }
    } catch {
        /* unpublished or unreachable */
    }

    return null;
}

/**
 * Certifications. FIRST priority from API.
 * Falls back to local static data only if the API is unreachable.
 */
export async function loadCertifications(): Promise<Certification[]> {
    if (cachedCertifications) return cachedCertifications;

    try {
        const rows = await getCertifications();
        if (Array.isArray(rows) && rows.length > 0) {
            const usable = rows
                .filter((r) => r?.standard && r?.covers)
                .map((r) => ({
                    standard: r.standard,
                    covers: r.covers,
                    certificateNumber: r.certificateNumber ?? "",
                    issuedBy: r.issuedBy ?? "",
                    validUntil: r.validUntil ?? "",
                    verifyUrl: r.verifyUrl ?? "",
                    documentUrl: r.documentUrl ?? "",
                }));
            if (usable.length > 0) {
                cachedCertifications = usable;
                return usable;
            }
        }
    } catch (err) {
        console.warn("Certifications API unreachable, falling back to local static data:", err);
    }

    return CERTIFICATIONS;
}
